/**
 * Component: sa-review
 * Description: Review Carousel Section
 */
if (!customElements.get('sa-review')) {
  class SaReview extends HTMLElement {
    constructor() {
      super();
      this.handleClickPrev = this.handleClickPrev.bind(this);
      this.handleClickNext = this.handleClickNext.bind(this);
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleResize = this.handleResize.bind(this);
      
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);

      this.currentIndex = 0;
      this.intervalId = null;
      this.isHovering = false;
      
      this.touchStartX = 0;
      this.touchStartY = 0;
    }

    connectedCallback() {
      this.track = this.querySelector('.sa-review__track');
      this.items = this.querySelectorAll('.sa-review__item');
      this.prevBtn = this.querySelector('.sa-review__btn--prev');
      this.nextBtn = this.querySelector('.sa-review__btn--next');
      
      if (!this.track || this.items.length === 0) return;

      this.autoplay = this.getAttribute('data-autoplay') === 'true';
      this.interval = parseInt(this.getAttribute('data-interval')) || 3000;

      this.initCarousel();
      this.addEventListeners();
      
      if (this.autoplay) {
        this.startAutoPlay();
      }
      
      console.log('SaReview loaded');
    }

    disconnectedCallback() {
      this.stopAutoPlay();
      this.removeEventListeners();
    }

    initCarousel() {
      // Use requestAnimationFrame to ensure styles are applied and dimensions are correct
      requestAnimationFrame(() => {
        this.updateDimensions();
        this.updatePosition();
      });
    }

    updateDimensions() {
      const containerWidth = this.querySelector('.sa-review__carousel').offsetWidth;
      const itemWidth = this.items[0].offsetWidth;
      const gap = parseInt(window.getComputedStyle(this.track).gap) || 0;
      const step = itemWidth + gap;
      
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        this.visibleItems = 1;
        this.maxIndex = Math.max(0, this.items.length - 1);
      } else {
        this.visibleItems = Math.floor((containerWidth + gap) / step);
        if (this.visibleItems < 1) this.visibleItems = 1;
        this.maxIndex = Math.max(0, this.items.length - this.visibleItems);
      }
      
      // Reset index if out of bounds after resize
      if (this.currentIndex > this.maxIndex) {
        this.currentIndex = this.maxIndex;
      }
    }

    addEventListeners() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', this.handleClickPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.handleClickNext);
      
      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
      window.addEventListener('resize', this.handleResize);

      this.track.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.track.addEventListener('touchend', this.handleTouchEnd);
    }

    removeEventListeners() {
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.handleClickPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.handleClickNext);
      
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
      window.removeEventListener('resize', this.handleResize);

      this.track.removeEventListener('touchstart', this.handleTouchStart);
      this.track.removeEventListener('touchend', this.handleTouchEnd);
    }

    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
      this.touchStartY = e.changedTouches[0].screenY;
      this.stopAutoPlay();
    }

    handleTouchEnd(e) {
      const touchEndX = e.changedTouches[0].screenX;
      const touchEndY = e.changedTouches[0].screenY;
      
      const diffX = touchEndX - this.touchStartX;
      const diffY = touchEndY - this.touchStartY;
      
      // Horizontal swipe detection (threshold: 50px)
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
        if (diffX > 0) {
          this.prev();
        } else {
          this.next();
        }
      }

      if (this.autoplay) {
        this.startAutoPlay();
      }
    }

    handleResize() {
      // Debounce could be added here
      this.updateDimensions();
      this.updatePosition();
    }

    handleClickPrev(e) {
      e.preventDefault();
      this.prev();
    }

    handleClickNext(e) {
      e.preventDefault();
      this.next();
    }

    handleMouseEnter() {
      this.isHovering = true;
      this.stopAutoPlay();
    }

    handleMouseLeave() {
      this.isHovering = false;
      if (this.autoplay) {
        this.startAutoPlay();
      }
    }

    next() {
      if (this.currentIndex < this.maxIndex) {
        this.currentIndex++;
      } else {
        // Loop back to start
        this.currentIndex = 0;
      }
      this.updatePosition();
    }

    prev() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
      } else {
        // Loop to end
        this.currentIndex = this.maxIndex;
      }
      this.updatePosition();
    }

    updatePosition() {
      const containerWidth = this.querySelector('.sa-review__carousel').offsetWidth;
      const itemWidth = this.items[0].offsetWidth;
      const gap = parseInt(window.getComputedStyle(this.track).gap) || 0;
      const step = itemWidth + gap;
      
      const isMobile = window.innerWidth <= 768;
      let offset = 0;

      if (isMobile) {
        offset = (containerWidth - itemWidth) / 2;
      }
      
      const translate = -(this.currentIndex * step) + offset;
      this.track.style.transform = `translateX(${translate}px)`;
    }

    startAutoPlay() {
      if (this.intervalId) clearInterval(this.intervalId);
      this.intervalId = setInterval(() => {
        if (!this.isHovering) {
          this.next();
        }
      }, this.interval);
    }

    stopAutoPlay() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }
  }

  customElements.define('sa-review', SaReview);
}
